import { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import type { ReactNode } from 'react';
import { batchingService } from '../../pages/BatchReportingPage/services/batchingService';
import type { BatchingStudy } from '../../pages/BatchReportingPage/services/batchingService';

export interface BatchEditState {
  /** Whether we are in batch editing mode */
  isBatchMode: boolean;
  /** Mongo _id of the batching document */
  batchId: string | null;
  /** All studies remaining in this batch */
  studies: BatchingStudy[];
  /** Total number of studies that were originally in the batch */
  totalStudies: number;
  /** Index of the study currently being edited (0-based) */
  currentIndex: number;
  /** The descriptive text for the current study */
  currentDescriptive: string | null;
  /** The patient id for the current study */
  currentPatientId: string | null;
  /** The mongo_template_id for the current study (if matched) */
  currentMongoTemplateId: string | null;
  /** Whether data is being loaded */
  isLoading: boolean;
}

export interface BatchEditContextValue extends BatchEditState {
  /** Initialize batch mode with a batchId — fetches data from API */
  initBatch: (batchId: string) => Promise<void>;
  /** Called after report generation — removes current study from the batch collection */
  onStudyGenerated: () => Promise<void>;
  /** Called after "send"/save — advances to the next study or exits batch mode */
  advanceToNextStudy: () => boolean;
  /** Exit batch mode */
  exitBatch: () => void;
  /** Label for the header: "Lote 1 de 5" */
  batchLabel: string;
}

const defaultState: BatchEditContextValue = {
  isBatchMode: false,
  batchId: null,
  studies: [],
  totalStudies: 0,
  currentIndex: 0,
  currentDescriptive: null,
  currentPatientId: null,
  currentMongoTemplateId: null,
  isLoading: false,
  initBatch: async () => {},
  onStudyGenerated: async () => {},
  advanceToNextStudy: () => false,
  exitBatch: () => {},
  batchLabel: '',
};

const BatchEditContext = createContext<BatchEditContextValue>(defaultState);

export function BatchEditProvider({ children }: { children: ReactNode }) {
  const [batchId, setBatchId] = useState<string | null>(null);
  const [studies, setStudies] = useState<BatchingStudy[]>([]);
  const [totalStudies, setTotalStudies] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Track which original index we've processed (for the "Lote X de Y" label)
  const processedCountRef = useRef(0);

  const isBatchMode = batchId !== null;
  const currentStudy = studies.length > 0 ? studies[0] : null;
  const currentDescriptive = currentStudy?.descriptivo ?? null;
  const currentPatientId = currentStudy?.id_paciente ?? null;
  const currentMongoTemplateId = currentStudy?.mongo_template_id ?? null;

  // Label: "Lote 1 de 5", "Lote 2 de 5", etc.
  const batchLabel = isBatchMode
    ? `Lote ${processedCountRef.current + 1} de ${totalStudies}`
    : '';

  const initBatch = useCallback(async (id: string) => {
    setIsLoading(true);
    try {
      const batch = await batchingService.getById(id);
      setBatchId(id);
      setStudies(batch.studies);
      setTotalStudies(batch.studies.length);
      setCurrentIndex(0);
      processedCountRef.current = 0;
    } catch (error) {
      console.error('Error loading batch:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onStudyGenerated = useCallback(async () => {
    if (!batchId || studies.length === 0) return;

    try {
      // Remove the first study (index 0) from the MongoDB collection
      const result = await batchingService.removeStudy(batchId, 0);

      if (result === null) {
        // Batch is empty — will handle in advanceToNextStudy
        setStudies([]);
      } else {
        setStudies(result.studies);
      }
    } catch (error) {
      console.error('Error removing study from batch:', error);
    }
  }, [batchId, studies]);

  const advanceToNextStudy = useCallback((): boolean => {
    processedCountRef.current += 1;

    if (studies.length === 0) {
      // No more studies, batch is done
      setBatchId(null);
      setStudies([]);
      setTotalStudies(0);
      setCurrentIndex(0);
      processedCountRef.current = 0;
      return false;
    }

    // studies already has the first element removed by onStudyGenerated
    // so studies[0] is now the next study
    setCurrentIndex(prev => prev + 1);
    return true;
  }, [studies]);

  const exitBatch = useCallback(() => {
    setBatchId(null);
    setStudies([]);
    setTotalStudies(0);
    setCurrentIndex(0);
    processedCountRef.current = 0;
  }, []);

  const value = useMemo(() => ({
        isBatchMode,
        batchId,
        studies,
        totalStudies,
        currentIndex,
        currentDescriptive,
        currentPatientId,
        currentMongoTemplateId,
        isLoading,
        initBatch,
        onStudyGenerated,
        advanceToNextStudy,
        exitBatch,
        batchLabel,
      }), [isBatchMode, batchId, studies, totalStudies, currentIndex,
           currentDescriptive, currentPatientId, currentMongoTemplateId,
           isLoading, initBatch, onStudyGenerated, advanceToNextStudy,
           exitBatch, batchLabel]);

  return (
    <BatchEditContext.Provider
      value={value}
    >
      {children}
    </BatchEditContext.Provider>
  );
}

export function useBatchEdit(): BatchEditContextValue {
  return useContext(BatchEditContext);
}
